import { useEffect } from 'react';

interface UseKeyboardProps {
  isOpen: boolean;
  onClose: () => void;
  play: () => void;
  pause: () => void;
  isPlaying: boolean;
  seek: (time: number) => void;
  currentTime: number;
  duration: number;
  setVolume: (volume: number) => void;
  volume: number;
  toggleMute: () => void;
  toggleFullscreen: () => void;
}

export const useKeyboard = ({
  isOpen,
  onClose,
  play,
  pause,
  isPlaying,
  seek,
  currentTime,
  duration,
  setVolume,
  volume,
  toggleMute,
  toggleFullscreen
}: UseKeyboardProps) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Let focused controls and links handle their own keyboard activation.
      if (e.key !== 'Escape' && e.target instanceof Element &&
        e.target.closest('button, a, input, select, textarea, [role="button"], [contenteditable="true"]')) {
        return;
      }

      // Prevent default for keys we're handling
      const preventDefaultKeys = [' ', 'Escape', 'f', 'F', 'm', 'M', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
      if (preventDefaultKeys.includes(e.key)) {
        e.preventDefault();
      }

      switch (e.key) {
        case ' ':
        case 'Spacebar':
          // Play/Pause
          if (isPlaying) {
            pause();
          } else {
            play();
          }
          break;

        case 'Escape':
          // Close portal
          onClose();
          break;

        case 'f':
        case 'F':
          // Fullscreen
          toggleFullscreen();
          break;

        case 'm':
        case 'M':
          // Mute
          toggleMute();
          break;

        case 'ArrowLeft':
          // Seek backward 10 seconds
          seek(Math.max(0, currentTime - 10));
          break;

        case 'ArrowRight':
          // Seek forward 10 seconds
          seek(Math.min(duration, currentTime + 10));
          break;

        case 'ArrowUp':
          // Increase volume
          setVolume(Math.min(1, volume + 0.1));
          break;

        case 'ArrowDown':
          // Decrease volume
          setVolume(Math.max(0, volume - 0.1));
          break;

        default:
          // Number keys for percentage seeking
          if (e.key >= '0' && e.key <= '9') {
            const percentage = parseInt(e.key) / 10;
            seek(duration * percentage);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    isOpen,
    onClose,
    play,
    pause,
    isPlaying,
    seek,
    currentTime,
    duration,
    setVolume,
    volume,
    toggleMute,
    toggleFullscreen
  ]);
};
